import { useMemo } from 'react';
import { useAppSwitcherContext } from '../components/AppSwitcherProvider';
import { useOrgContext } from '../components/org/OrgProvider';
import { useLanguage } from '../i18n/LanguageProvider';
import { useAppSidecarSidebarEntries } from '../sidecars/AppSidecarSidebarEntries';
import { OrgRoute } from './useRoute';

export interface NavOption {
  label: string;
  value: OrgRoute;
  route: string;
  icon?: string; // tabler icon name for sidebar
}

export const useNavigationOptions = (): NavOption[] => {
  const { messages } = useLanguage();
  const { app } = useAppSwitcherContext();
  const { org } = useOrgContext();
  // Sidecar nav entries are sourced from
  // `<AppSidecarSidebarEntries>` (called as a hook here for
  // spread-friendly composition; same pattern `RakineMainModules`
  // uses for `useAppSidecarTiles`). Each sidecar declares its own
  // sidebar metadata in `surfaces.tsx`; the (appId, sidecarKey) →
  // metadata lookup lives in `client/src/sidecars/tiles.ts`. Adding
  // a new sidecar's left-nav entry is a one-line registry add — no
  // edit to this file required for the rakine case.
  const rakineSidecarEntries = useAppSidecarSidebarEntries('rakine');

  return useMemo(() => {
    if (!org) return [];

    const id = org.id;

    switch (app) {
      case 'clientell':
        return [
          {
            label: messages.nav.clients,
            value: 'clients' as OrgRoute,
            route: `/orgs/${id}/clients/view`,
            icon: 'user',
          },
          {
            label: messages.nav.groups,
            value: 'groups' as OrgRoute,
            route: `/orgs/${id}/groups/view`,
            icon: 'users',
          },
          {
            label: messages.nav.templates,
            value: 'templates' as OrgRoute,
            route: `/orgs/${id}/templates/view`,
            icon: 'message',
          },
        ];
      case 'imminote':
        return [
          {
            label:
              messages.imminote?.modules?.parametres || messages.nav.settings,
            value: 'settings' as OrgRoute,
            route: `/orgs/${id}/settings`,
            icon: 'settings',
          },
          {
            label: messages.imminote?.modules?.messages || 'Messages',
            value: 'messages' as OrgRoute,
            route: `/orgs/${id}/messages`,
            icon: 'mail',
          },
          {
            label: messages.imminote?.modules?.apiDocs || 'API Documentation',
            value: 'api-docs' as OrgRoute,
            route: `/orgs/${id}/api-docs`,
            icon: 'book',
          },
        ];
      case 'rakine':
        return [
          {
            label: messages.rakine?.modules?.overview || 'Overview',
            value: 'overview' as OrgRoute,
            route: `/orgs/${id}/overview`,
            icon: 'dashboard',
          },
          {
            label: messages.rakine?.modules?.drivers || 'Drivers',
            value: 'drivers' as OrgRoute,
            route: `/orgs/${id}/drivers`,
            icon: 'truck',
          },
          {
            label: messages.rakine?.modules?.deliveries || 'Deliveries',
            value: 'deliveries' as OrgRoute,
            route: `/orgs/${id}/deliveries`,
            icon: 'package',
          },
          {
            label: messages.rakine?.modules?.dispatch || 'Dispatch',
            value: 'dispatch' as OrgRoute,
            route: `/orgs/${id}/dispatch`,
            icon: 'send',
          },
          {
            label: messages.rakine?.modules?.settings || 'Settings',
            value: 'settings' as OrgRoute,
            route: `/orgs/${id}/settings`,
            icon: 'settings',
          },
          // Sidecar entries appear AFTER core nav so the core ordering
          // is stable for orgs without any sidecar enabled, and the
          // sidecars cluster together at the bottom of the nav (the
          // visual divider in `Sidebar.tsx` separates this whole
          // block from the utilities below). Empty when no sidecars
          // match — see `useAppSidecarSidebarEntries`.
          ...rakineSidecarEntries,
        ];
      case 'whatchat':
        return [
          {
            label: messages.whatchat?.modules?.inbox || 'Inbox',
            value: 'inbox' as OrgRoute,
            route: `/orgs/${id}/inbox`,
            icon: 'inbox',
          },
          {
            label: messages.whatchat?.modules?.metrics || 'Metrics',
            value: 'metrics' as OrgRoute,
            route: `/orgs/${id}/metrics`,
            icon: 'chart-bar',
          },
        ];
      case 'botmaker':
        return [
          {
            label: messages.botFlows?.title || 'Bot Flows',
            value: 'flows' as OrgRoute,
            route: `/orgs/${id}/flows`,
            icon: 'binary-tree',
          },
        ];
      default:
        return [];
    }
  }, [app, org, messages, rakineSidecarEntries]);
};
